import { absoluteCentre, marginBottom, marginLeft, paddingLeft, typography } from "../../common/styles";
import { AppContext, BookingData, BookingQuestionData, BookingQuestionFileNameData, BookingQuestionValue, ExternalBookingQuestion } from "../../AppDataProvider/AppDataProvider";
import { LoadingPill as BaseLoadingPill, Input, InputElement, NavButtons } from "../../common/components";
import { BookingQuestionFieldType, BookingQuestionSubquestion } from "../../GeneratedGraphQL";
import { getBookingQuestionsPreviousStep, requireBookingDataFields } from "../../utils";
import React, { useContext, useState } from "react";
import { ActionType } from "../../AppDataProvider/actions";
import { applyOpacity } from "../../common/styles/utils";
import { getInput } from "./getInput";
import { Mixpanel } from "../../mixpanel";
import { PartRequired } from "../../utils/types";
import { RequiredStar } from "../../common/components/Input/common";
import styled from "styled-components";
import { useApolloClient } from "@apollo/client";
import { useDeleteExistingReservedSpace } from "../Availability/hooks/useDeleteExistingReservedSpace";
import { useTranslation } from "react-i18next";
const VALID_TEXT_MAX_LENGTH = 750;
type BookingQuestionsStepBookingData = PartRequired<BookingData, "organisation" | "service">;
export type OnChangeBookingQuestionValueFunction = (args: {
  fileName?: string;
  id: string;
  newValue: BookingQuestionValue;
  removeFile?: boolean;
}) => void;
export function BookingQuestionsStep(): React.ReactElement {
  const client = useApolloClient();
  const {
    t
  } = useTranslation();
  const {
    appData,
    appDataDispatch
  } = useContext(AppContext);
  const {
    bookingData,
    bookingTool
  } = appData;
  const [bookingQuestionData, setBookingQuestionData] = useState<BookingQuestionData>(bookingData.bookingQuestionData ?? {});
  const urlParams = new URLSearchParams(window.location.search);
  const bookingNotesPrefill = decodeURIComponent(urlParams.get("notesPrefill") ?? "");
  const [bookingNotes, setBookingNotes] = useState<string>(bookingData.bookingNotes ?? bookingNotesPrefill);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [bookingQuestionFileNameData, setBookingQuestionFileNameData] = useState<BookingQuestionFileNameData>(bookingData.bookingQuestionFileNameData ?? {});
  const [deleteReservationLoading, setDeleteReservationLoading] = useState<boolean>(false);
  const [uploadsInProgress, setUploadsInProgress] = useState<string[]>([]);
  requireBookingDataFields<BookingQuestionsStepBookingData>(bookingData, ["organisation", "service"]);
  const {
    organisation,
    service,
    reservedSpaceId
  } = bookingData;
  const {
    allowBookingNotes,
    customBookingNotesText,
    externalBookingQuestions
  } = organisation;
  const deleteExistingReservedSpace = useDeleteExistingReservedSpace({
    organisationId: organisation._id,
    reservedSpaceId
  });

  // Filter out any questions with don't apply to the selected service.
  // If the question has no services list, it's applicable to all services.
  const questions = externalBookingQuestions.filter(question => {
    const validServiceIds = question.services?.map(service => service._id);
    return validServiceIds?.includes(service._id) ?? true;
  });
  const hasRequiredFields = questions.some(question => {
    const {
      subquestions
    } = question;
    return !!question.required || subquestions && subquestions.some(subQuestion => subQuestion.required);
  });
  const isSubquestionDisplayed = ({
    parent,
    subquestion
  }: {
    parent: ExternalBookingQuestion;
    subquestion: BookingQuestionSubquestion;
  }): boolean => {
    const id = parent.id ?? parent.name;
    const parentValue = bookingQuestionData[id];
    const {
      dropdownTriggers
    } = subquestion;
    let selectedOptionId = undefined;
    if (parent.type === BookingQuestionFieldType.Dropdown && parent.options) {
      const selectedOption = parent.options.find(option => option.value === parentValue);
      selectedOptionId = selectedOption?.optionId;
    }
    return !!(parent.type === BookingQuestionFieldType.Checkbox && parentValue || parent.type === BookingQuestionFieldType.Dropdown && selectedOptionId && dropdownTriggers?.includes(selectedOptionId));
  };
  const onChange: OnChangeBookingQuestionValueFunction = ({
    id,
    newValue,
    fileName,
    removeFile
  }) => {
    const newBookingQuestionData: BookingQuestionData = {
      ...bookingQuestionData,
      [id]: newValue
    };
    setBookingQuestionData(newBookingQuestionData);
    if (fileName && !removeFile) {
      const newBookingQuestionFileNameData: BookingQuestionFileNameData = {
        ...bookingQuestionFileNameData,
        [id]: fileName
      };
      setBookingQuestionFileNameData(newBookingQuestionFileNameData);
    } else if (removeFile) {
      const newBookingQuestionData: BookingQuestionData = {
        ...bookingQuestionData
      };
      delete newBookingQuestionData[id];
      setBookingQuestionData(newBookingQuestionData);
      const newBookingQuestionFileNameData: BookingQuestionFileNameData = {
        ...bookingQuestionFileNameData
      };
      delete newBookingQuestionFileNameData[id];
      setBookingQuestionFileNameData(newBookingQuestionFileNameData);
    }
    const newErrors = {
      ...errors
    };
    delete newErrors[id];
    setErrors(newErrors);
  };
  const completeStep = () => {
    const newErrors: Record<string, string> = {};
    questions.forEach(question => {
      const {
        required,
        subquestions
      } = question;
      const id = question.id ?? question.name;
      const value = bookingQuestionData[id];
      // Simple falsy check on value covers non-existant (unanswered) questions
      // as well as checkboxes which are false and empty string answers.
      if (required && !value) newErrors[id] = t("errors.required");
      if (value && typeof value === "string" && value.length > VALID_TEXT_MAX_LENGTH) newErrors[id] = t("errors.textMaxCharacters", {
        max: VALID_TEXT_MAX_LENGTH
      });
      const displayedSubquestions = subquestions?.filter(subquestion => isSubquestionDisplayed({
        parent: question,
        subquestion
      })) ?? [];
      displayedSubquestions.forEach(({
        id,
        required
      }) => {
        const value = bookingQuestionData[id];
        if (required && !value) newErrors[id] = t("errors.required");
      });
    });
    uploadsInProgress.forEach(id => newErrors[id] = t("errors.uploadInProgress"));
    setErrors(newErrors);
    if (Object.keys(newErrors).length !== 0) {
      Mixpanel.track({
        name: "Form validation error",
        props: {
          errors: newErrors,
          step: "bookingQuestions"
        }
      });
      return;
    }
    appDataDispatch({
      payload: {
        updatedData: {
          bookingNotes,
          bookingQuestionData,
          bookingQuestionFileNameData
        }
      },
      type: ActionType.COMPLETE_BOOKING_QUESTIONS_STEP
    });
  };
  const isPreviousStepAvailability = getBookingQuestionsPreviousStep({
    bookingData,
    bookingTool,
    organisation
  }) === "availability" || getBookingQuestionsPreviousStep({
    bookingData,
    bookingTool,
    organisation
  }) === "locationAvailability";
  const backFromStep = () => {
    const newErrors: Record<string, string> = {};
    uploadsInProgress.forEach(id => newErrors[id] = t("errors.uploadInProgress"));
    setErrors(newErrors);
    if (Object.keys(newErrors).length !== 0) return;
    const onBackCleanup = async () => {
      if (isPreviousStepAvailability && reservedSpaceId) {
        setDeleteReservationLoading(true);
        await deleteExistingReservedSpace();
        setDeleteReservationLoading(false);
      }
      await client.clearStore();
      appDataDispatch({
        payload: {
          updatedData: {
            bookingNotes,
            bookingQuestionData
          }
        },
        type: ActionType.BACK_FROM_BOOKING_QUESTIONS_STEP
      });
    };
    void onBackCleanup();
  };
  if (deleteReservationLoading || !reservedSpaceId) return <LoadingPill />;
  return <>
      <Form id="bookingQuestions" aria-label={t("bookingQuestionsAriaLabel")} data-sentry-element="Form" data-sentry-source-file="BookingQuestionsStep.tsx">
        {questions.map(question => {
        const {
          subquestions
        } = question;
        const id = question.id ?? question.name;
        const error = errors[id];
        const setError = (error: string) => {
          setErrors({
            ...errors,
            [id]: error
          });
        };
        const onStartUpload = () => {
          if (!uploadsInProgress.includes(id)) setUploadsInProgress([...uploadsInProgress, id]);
        };
        const onStopUpload = () => {
          const newUploadsInProgress = [...uploadsInProgress];
          newUploadsInProgress.splice(newUploadsInProgress.indexOf(id), 1);
          setUploadsInProgress(newUploadsInProgress);
          setError("");
        };
        const value = bookingQuestionData[id];
        const fileName = bookingQuestionFileNameData[id];
        const subQuestionsToDisplay = subquestions?.filter(subquestion => isSubquestionDisplayed({
          parent: question,
          subquestion
        }));
        return <div key={id}>
              {getInput({
            error,
            fileName,
            onChange,
            onStartUpload,
            onStopUpload,
            question,
            setError,
            value
          })}

              {subQuestionsToDisplay?.length ? <Subquestions>
                  {subQuestionsToDisplay.map(subQuestion => {
              const subQuestionError = errors[subQuestion.id];
              const setSubQuestionError = (error: string) => {
                setErrors({
                  ...errors,
                  [subQuestion.id]: error
                });
              };
              const onStartSubQuestionUpload = () => {
                if (!uploadsInProgress.includes(subQuestion.id)) setUploadsInProgress([...uploadsInProgress, subQuestion.id]);
              };
              const onStopSubQuestionUpload = () => {
                const newUploadsInProgress = [...uploadsInProgress];
                newUploadsInProgress.splice(newUploadsInProgress.indexOf(subQuestion.id), 1);
                setUploadsInProgress(newUploadsInProgress);
                setSubQuestionError("");
              };
              const subQuestionValue = bookingQuestionData[subQuestion.id];
              return <div key={subQuestion.id}>
                        {getInput({
                  error: subQuestionError,
                  onChange,
                  onStartUpload: onStartSubQuestionUpload,
                  onStopUpload: onStopSubQuestionUpload,
                  question: subQuestion,
                  setError: setSubQuestionError,
                  value: subQuestionValue
                })}
                      </div>;
            })}
                </Subquestions> : null}
            </div>;
      })}
        {allowBookingNotes && <Input variant="text" label={customBookingNotesText ? customBookingNotesText : t("bookingNotesLabel")} id="bookingNotes" rows={3} value={bookingNotes} onChange={(event: React.ChangeEvent<InputElement>) => {
        setBookingNotes(event.target.value);
      }} />}
      </Form>

      {hasRequiredFields && <RequiredFields>
          <RequiredStar />
          <RequiredFieldsText>{t("requiredFields")}</RequiredFieldsText>
        </RequiredFields>}

      <NavButtons {...{
      onBack: backFromStep,
      onContinue: completeStep
    }} data-sentry-element="NavButtons" data-sentry-source-file="BookingQuestionsStep.tsx" />
    </>;
}
const Form = styled.form`
  display: flex;
  flex-direction: column;
`;
const Subquestions = styled.fieldset`
  border: none;
  border-left: 2px solid ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.1)};
  ${paddingLeft.s}
  ${marginBottom.m}
`;
const RequiredFields = styled.div`
  display: flex;
  align-items: flex-start;
`;
const RequiredFieldsText = styled.div`
  ${typography.small}
  ${marginLeft.xs}
`;
const LoadingPill = styled(BaseLoadingPill)`
  ${absoluteCentre}
`;
export default BookingQuestionsStep;